import React from "react";
import Table from "@/shared/components/ui/table";
import { Tag } from "@/shared/components/ui/tag";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { Button } from "@/shared/components/ui/button";
import { MoreHorizontal, Pencil, Trash2, HelpCircle, ArrowUp, ArrowDown } from "lucide-react";
import { ContactStatusBadge } from "./ContactStatusBadge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import ConfirmDeleteDialog from "@/shared/components/overlay/ConfirmDeleteDialog";
import { ContactAvatar } from "@/shared/components/ContactAvatar";
import { getRelationshipLabel } from "@/shared/constants/relationshipOptions";
import { useUserContacts } from "@/shared/hooks/useUserContacts";
import { formatLegalRole } from "@/shared/utils/formatters";
import { sentryUtils } from "@/shared/utils/sentry";
import { PersonContact } from "@/shared/types/estateContact";

import { ContactDialog } from "./ContactDialog";
import InviteConfirmationDialog from "./InviteConfirmationDialog";
import { isProfessionalContact } from "./contactUtils";

interface ContactRow {
  id: number;
  name: React.ReactNode;
  role: React.ReactNode;
  elayneAccess: React.ReactNode;
  actions: React.ReactNode;
}

interface PeopleTableProps {
  contacts: PersonContact[];
  isLoading: boolean;
  searchQuery?: string;
  deceasedUserData?: {
    firstName?: string;
    displayName?: string;
    surName?: string;
  } | null;
  onInviteContact: (contact: PersonContact) => Promise<void>;
  onResendInvitation: (contact: PersonContact) => Promise<void>;
  onCancelInvitation?: (invitationId: number) => Promise<void>;
  pendingInvitations?: Array<{
    id: number;
    email: string;
    invitation_token: string;
    role: string;
    expires_at: string;
    created_at: string;
  }>;
  acceptedInvitations?: Array<{
    id: number;
    email: string;
    invitation_token: string;
    role: string;
    expires_at: string;
    created_at: string;
    updated_at?: string;
  }>;
  currentUserEmail?: string;
  accountOwner?: {
    user_id: number;
    email: string;
    first_name: string | null;
    last_name: string | null;
  } | null;
  resendingInvitationEmail?: string | null;
  isSendingInvitation?: boolean;
  isSimulating?: boolean;
  onRemoveAccess?: (email: string) => Promise<void>;
}

const formatName = (firstName: string | null, lastName: string | null): string => {
  if (!firstName && !lastName) return "—";
  return [firstName, lastName].filter(Boolean).join(" ");
};

const PeopleTable: React.FC<PeopleTableProps> = ({
  contacts,
  isLoading,
  searchQuery,
  deceasedUserData,
  onInviteContact,
  onResendInvitation,
  onCancelInvitation,
  pendingInvitations,
  acceptedInvitations,
  currentUserEmail,
  accountOwner,
  resendingInvitationEmail,
  isSendingInvitation = false,
  isSimulating = false,
  onRemoveAccess,
}) => {
  const [isEditContactOpen, setIsEditContactOpen] = React.useState(false);
  const [contactToEditId, setContactToEditId] = React.useState<number | null>(null);
  const contactToEdit = contacts.find((c) => c.id === contactToEditId) ?? null;
  const [initialEmailError, setInitialEmailError] = React.useState<string | undefined>(undefined);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);
  const [contactToDelete, setContactToDelete] = React.useState<PersonContact | null>(null);
  const [isInviteDialogOpen, setIsInviteDialogOpen] = React.useState(false);
  const [contactToInvite, setContactToInvite] = React.useState<PersonContact | null>(null);
  const [pendingInviteAfterEdit, setPendingInviteAfterEdit] = React.useState(false);
  const [familySortAsc, setFamilySortAsc] = React.useState(true);
  const [professionalSortAsc, setProfessionalSortAsc] = React.useState(true);

  const { deleteContact, isDeleting, updateContactAsync } = useUserContacts();

  const handleEditContact = (contact: PersonContact) => {
    setContactToEditId(contact.id);
    setInitialEmailError(undefined);
    setIsEditContactOpen(true);
  };

  const handleSaveContact = (updatedContact?: { id: number; email: string | null }) => {
    if (pendingInviteAfterEdit && updatedContact?.email) {
      const contact = contacts.find((c) => c.id === updatedContact.id);
      if (contact) {
        setContactToInvite({ ...contact, email: updatedContact.email });
        setIsInviteDialogOpen(true);
      }
    }
    setPendingInviteAfterEdit(false);
    setInitialEmailError(undefined);
  };

  const handleEditDialogOpenChange = (open: boolean) => {
    setIsEditContactOpen(open);
    if (!open) {
      setInitialEmailError(undefined);
      setPendingInviteAfterEdit(false);
    }
  };

  const handleDeleteClick = (contact: PersonContact) => {
    setContactToDelete(contact);
    setIsDeleteDialogOpen(true);
  };

  const handleInviteClick = (contact: PersonContact) => {
    if (!contact.email) {
      setContactToEditId(contact.id);
      setInitialEmailError("This contact needs an email before they can be invited.");
      setIsEditContactOpen(true);
      return;
    }
    setContactToInvite(contact);
    setIsInviteDialogOpen(true);
  };

  const handleConfirmInvite = async () => {
    if (!contactToInvite) return;

    try {
      await onInviteContact(contactToInvite);
      setIsInviteDialogOpen(false);
      setContactToInvite(null);
    } catch (error) {
      console.error("Failed to send invitation:", error);
      sentryUtils.captureError(error, "Failed to send invitation");
    }
  };

  const handleConfirmDelete = () => {
    if (!contactToDelete) return;

    try {
      deleteContact(contactToDelete.id);
      setIsDeleteDialogOpen(false);
      setContactToDelete(null);
    } catch (error) {
      console.error("Failed to delete contact:", error);
      sentryUtils.captureError(error, "Failed to delete contact");
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteDialogOpen(false);
    setContactToDelete(null);
  };

  const makeColumns = (
    sortAsc: boolean,
    setSortAsc: React.Dispatch<React.SetStateAction<boolean>>,
  ) => [
    {
      header: (
        <Button
          variant="ghost"
          size="sm"
          className="flex items-center gap-1 p-0 h-auto hover:bg-transparent"
          onClick={() => setSortAsc((prev) => !prev)}
        >
          <span>Name</span>
          {sortAsc ? (
            <ArrowUp aria-hidden="true" className="h-3.5 w-3.5 text-primary" />
          ) : (
            <ArrowDown aria-hidden="true" className="h-3.5 w-3.5 text-primary" />
          )}
        </Button>
      ),
      accessor: "name" as keyof ContactRow,
      className: "min-w-[200px] max-w-[300px] overflow-hidden",
      thProps: {
        "aria-sort": (sortAsc ? "ascending" : "descending") as "ascending" | "descending",
      },
    },
    {
      header: (
        <div className="flex items-center gap-2">
          <span>Role</span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <HelpCircle className="h-4 w-4 text-tertiary" />
              </TooltipTrigger>
              <TooltipContent>
                The person's official role in {deceasedUserData?.displayName || "the decedent"}'s
                estate, such as Executor, Beneficiary, or Trustee.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      ),
      accessor: "role" as keyof ContactRow,
      className: "min-w-[200px]",
    },
    {
      header: "Elayne access",
      accessor: "elayneAccess" as keyof ContactRow,
      className: "min-w-[180px]",
    },
    {
      header: "",
      accessor: "actions" as keyof ContactRow,
      className: "w-[60px]",
    },
  ];

  const familyColumns = makeColumns(familySortAsc, setFamilySortAsc);
  const professionalColumns = makeColumns(professionalSortAsc, setProfessionalSortAsc);

  const skeletonRows: ContactRow[] = Array.from({ length: 5 }, (_, index) => ({
    id: index,
    name: (
      <div className="flex items-center gap-3 h-full">
        <Skeleton className="h-8 w-8 rounded-full" />
        <Skeleton className="h-4 w-32" />
      </div>
    ),
    role: (
      <div className="flex items-center h-full">
        <Skeleton className="h-4 w-24" />
      </div>
    ),
    elayneAccess: (
      <div className="flex items-center h-full">
        <Skeleton className="h-4 w-20" />
      </div>
    ),
    actions: (
      <div className="flex items-center h-full">
        <Skeleton className="h-4 w-4" />
      </div>
    ),
  }));

  const contactRowMap = React.useMemo((): Map<number, ContactRow> => {
    const map = new Map<number, ContactRow>();

    contacts.forEach((contact) => {
      const pendingInvitation = pendingInvitations?.find((inv) => inv.email === contact.email);
      const acceptedInvitation = acceptedInvitations?.find((inv) => inv.email === contact.email);
      const isAccountOwner =
        accountOwner && accountOwner.email?.toLowerCase() === contact.email?.toLowerCase();
      const isCurrentUser = currentUserEmail === contact.email;

      const relationshipLabel = contact.relationship
        ? getRelationshipLabel(contact.relationship)
        : null;

      const hasStatus =
        isAccountOwner || acceptedInvitation || pendingInvitation || contact.invited;

      const statusNode = hasStatus ? (
        <ContactStatusBadge
          isAccountOwner={!!isAccountOwner}
          acceptedInvitation={!!acceptedInvitation}
          pendingInvitation={!!pendingInvitation}
          isInvited={!!contact.invited}
          onResendInvitation={() => onResendInvitation(contact)}
          isResendingInvitation={resendingInvitationEmail === contact.email}
        />
      ) : isCurrentUser ? (
        <span className="text-sm text-stone-500">Me</span>
      ) : (
        <Button
          variant="outline"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            handleInviteClick(contact);
          }}
          className="h-[36px]"
          disabled={isSimulating}
          title={isSimulating ? "View-only mode during simulation" : undefined}
        >
          Invite
        </Button>
      );

      map.set(contact.id, {
        id: contact.id,
        name: (
          <div className="flex items-center gap-3 h-full min-w-0">
            <div className="flex-shrink-0">
              <ContactAvatar contact={contact} />
            </div>
            <div className="text-sm text-primary flex flex-col justify-center min-w-0">
              <div className="font-medium flex items-center gap-2 min-w-0">
                <span className="truncate">
                  {formatName(contact.first_name, contact.last_name)}
                  {isCurrentUser && <span className="ml-2 font-normal text-stone-500">(Me)</span>}
                </span>
                {contact.extracted_from_document && contact.source_document && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>
                          <Tag color="stone" size="sm">
                            From docs
                          </Tag>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-white">
                          Extracted from{" "}
                          {contact.source_document.display_name ||
                            contact.source_document.file_name}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
              {relationshipLabel && (
                <div className="text-sm text-secondary">{relationshipLabel}</div>
              )}
            </div>
          </div>
        ),
        role: (() => {
          const legalRoleText = formatLegalRole(contact.legal_role);
          const label =
            legalRoleText !== "—"
              ? legalRoleText
              : isProfessionalContact(contact) && contact.relationship
                ? getRelationshipLabel(contact.relationship)
                : "—";
          return <span className={`text-sm ${label === "—" ? "text-tertiary" : ""}`}>{label}</span>;
        })(),
        elayneAccess: statusNode,
        actions: (
          <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-transparent">
                  <MoreHorizontal className="h-4 w-4 text-tertiary hover:text-primary transition-colors" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-32">
                <DropdownMenuItem
                  onClick={() => handleEditContact(contact)}
                  className="cursor-pointer"
                  disabled={isSimulating}
                >
                  <Pencil className="mr-2 h-4 w-4" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleDeleteClick(contact)}
                  className="cursor-pointer text-red-600 hover:text-red-700 hover:bg-red-50"
                  disabled={isSimulating}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ),
      });
    });

    return map;
  }, [
    contacts,
    pendingInvitations,
    acceptedInvitations,
    accountOwner,
    currentUserEmail,
    resendingInvitationEmail,
    isSimulating,
    onResendInvitation,
  ]);

  const sortByNameWithMeFirst = (asc: boolean) => (a: PersonContact, b: PersonContact) => {
    const aIsMe = a.email === currentUserEmail;
    const bIsMe = b.email === currentUserEmail;

    if (aIsMe && !bIsMe) return -1;
    if (!aIsMe && bIsMe) return 1;

    const nameA = formatName(a.first_name, a.last_name).toLowerCase();
    const nameB = formatName(b.first_name, b.last_name).toLowerCase();
    return asc ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
  };

  const familyContacts = contacts
    .filter((c) => !isProfessionalContact(c))
    .sort(sortByNameWithMeFirst(familySortAsc));
  const professionalContacts = contacts
    .filter(isProfessionalContact)
    .sort(sortByNameWithMeFirst(professionalSortAsc));

  const toRows = (filtered: PersonContact[]): ContactRow[] =>
    filtered
      .map((c) => contactRowMap.get(c.id))
      .filter((row): row is ContactRow => row !== undefined);

  const familyRows = toRows(familyContacts);
  const professionalRows = toRows(professionalContacts);

  const hasContacts = contacts.length > 0;
  const shouldShowSkeletons = isLoading;

  const hasSearch = !!searchQuery;
  const isEmptyState = !isLoading && !hasContacts && !hasSearch;
  const isNoResultsState = !isLoading && !hasContacts && hasSearch;

  const tableClassName =
    "w-full overflow-auto [&_th]:pb-4 [&_th]:bg-white [&_th]:z-10 [&_th]:!align-middle [&_td]:px-4 sm:[&_td]:px-6 [&_td]:!py-4 [&_tr:not(:first-child)]:border-t [&_tr]:border-primary [&_thead]:border-b [&_thead]:border-primary [&_td]:!align-middle [&_td]:font-normal [&_th]:px-4 sm:[&_th]:px-6";

  const contactsById = React.useMemo(() => new Map(contacts.map((c) => [c.id, c])), [contacts]);

  const handleRowClick = shouldShowSkeletons
    ? undefined
    : (row: ContactRow) => {
        const contact = contactsById.get(row.id);
        if (contact) handleEditContact(contact);
      };

  return (
    <div className="w-full flex flex-col gap-6">
      {shouldShowSkeletons && (
        <div className="border border-primary rounded-lg overflow-hidden">
          <Table
            columns={familyColumns}
            data={skeletonRows}
            maxHeight="none"
            className={tableClassName}
            tableHeaderClassName="bg-white sticky top-0 z-10"
          />
        </div>
      )}

      {!isEmptyState && !isNoResultsState && !shouldShowSkeletons && (
        <>
          <div>
            <h3 className="text-base font-semibold mb-2">Family and beneficiaries</h3>
            <div className="border border-primary rounded-lg overflow-hidden">
              <Table
                columns={familyColumns}
                data={familyRows}
                maxHeight="none"
                className={tableClassName}
                tableHeaderClassName="bg-white sticky top-0 z-10"
                onRowClick={handleRowClick}
              />
              {familyRows.length === 0 && (
                <p className="p-6 text-center text-tertiary">
                  {hasSearch
                    ? "No family or beneficiaries match your search."
                    : "No family or beneficiaries added yet."}
                </p>
              )}
            </div>
          </div>

          <div>
            <h3 className="text-base font-semibold mb-2">Professionals</h3>
            <div className="border border-primary rounded-lg overflow-hidden">
              <Table
                columns={professionalColumns}
                data={professionalRows}
                maxHeight="none"
                className={tableClassName}
                tableHeaderClassName="bg-white sticky top-0 z-10"
                onRowClick={handleRowClick}
              />
              {professionalRows.length === 0 && (
                <p className="p-6 text-center text-tertiary">
                  {hasSearch
                    ? "No professionals match your search."
                    : "No professionals yet. An attorney or CPA can follow the roadmap with you and take work off your plate."}
                </p>
              )}
            </div>
          </div>
        </>
      )}

      {isNoResultsState && (
        <div className="py-10 flex flex-col items-center justify-center">
          <p className="text-base font-semibold mb-2">No contacts found</p>
          <p className="max-w-2xl text-center text-secondary">
            Try refining your search or adjusting your filters to find what you're looking for.
          </p>
        </div>
      )}

      {isEmptyState && (
        <div className="py-10 flex flex-col items-center justify-center">
          <p className="text-xl font-semibold mb-2">Add your first contact</p>
          <p className="max-w-2xl text-center text-secondary">
            Keep track of important people involved in the estate settlement process.
          </p>
        </div>
      )}

      {contactToEdit && (
        <ContactDialog
          isOpen={isEditContactOpen}
          onOpenChange={handleEditDialogOpenChange}
          contact={contactToEdit}
          deceasedUserData={deceasedUserData}
          onContactUpdated={handleSaveContact}
          onRemoveAccess={onRemoveAccess}
          initialEmailError={initialEmailError}
          pendingInvitation={(() => {
            if (!contactToEdit.email) return null;
            const emailLower = contactToEdit.email.toLowerCase();
            const inv = pendingInvitations?.find((i) => i.email === emailLower);
            return inv ? { id: inv.id, email: inv.email } : null;
          })()}
          acceptedInvitation={(() => {
            if (!contactToEdit.email) return null;
            const emailLower = contactToEdit.email.toLowerCase();
            const inv = acceptedInvitations?.find((i) => i.email === emailLower);
            return inv ? { created_at: inv.updated_at ?? inv.created_at } : null;
          })()}
          onInvite={async (email) => {
            if (!contactToEdit) return;
            if (contactToEdit.email !== email) {
              await updateContactAsync({ id: contactToEdit.id, email });
            }
            await onInviteContact({ ...contactToEdit, email } as PersonContact);
          }}
          onResendInvite={async (_id, email) => {
            const contact = contacts.find((c) => c.email === email);
            if (contact) await onResendInvitation(contact);
          }}
          onCancelInvite={onCancelInvitation}
          isInviting={isSendingInvitation}
        />
      )}

      <ConfirmDeleteDialog
        isOpen={isDeleteDialogOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title={`Delete ${contactToDelete?.first_name || "this contact"}`}
        description="This action is permanent and cannot be undone."
        isLoading={isDeleting}
      />

      <InviteConfirmationDialog
        isOpen={isInviteDialogOpen}
        onOpenChange={setIsInviteDialogOpen}
        contact={contactToInvite}
        onConfirm={handleConfirmInvite}
        isInviting={isSendingInvitation}
        deceasedUserData={deceasedUserData}
      />
    </div>
  );
};

export default PeopleTable;
